import type { Contact } from '@common/types/contacts';
import type { ContactFormValues } from '@common/types/formSubmissions';
import { pairEntriesWithContacts } from '@common/utils/contactPairing';

/** A party's slot in the contact step, with the party's contactId so a save sends it back */
export function contactToFormValues(
    contact: Contact,
    fallback?: ContactFormValues,
): ContactFormValues {
    return {
        ...fallback,
        contactId: contact.contactId,
        firstName: contact.firstName ?? fallback?.firstName ?? '',
        lastName: contact.lastName ?? fallback?.lastName ?? '',
        email: contact.contactEmail ?? fallback?.email ?? '',
        cellPhone: contact.phoneNumber ?? fallback?.cellPhone ?? '',
        middleName: contact.middleName ?? fallback?.middleName,
    };
}

/**
 * The contact step's slots for a resumed draft or post-intake form: each saved slot with its
 * party's live identity on top (the live row wins, so staff edits show), paired by contactId, or
 * by position for slots saved before they carried one (pairEntriesWithContacts). Parties without
 * a slot, such as ones staff added since, follow at the end. A slot without a party is kept.
 *
 * contactResponses has a row for every saved slot (overlayContactsOnContactFormValues), and a slot
 * without a party gets one without a contactId. Those rows are left out, so the slot isn't added
 * a second time.
 */
export function hydrateContactFormValues(
    savedContactFormValues: ContactFormValues[],
    contactResponses: Contact[],
): ContactFormValues[] {
    const { pairs, unpairedContacts } = pairEntriesWithContacts(
        savedContactFormValues,
        contactResponses.filter((contact) => !!contact.contactId),
    );

    return [
        ...pairs.map(({ entry, contact }) =>
            contact ? contactToFormValues(contact, entry) : entry,
        ),
        ...unpairedContacts.map((contact) => contactToFormValues(contact)),
    ];
}
